/**
 * Sprint P32 · Cockpit legend panels.
 * BlockLegend + DrillholeLegend — both adapt to the active colour-by mode.
 */
import { useMemo } from "react";
import { COLORMAPS, CLASS_HEX, PASS_HEX, ORE_HEX, LITHO_HEX, hashHex }
  from "../utils/colorPalettes";


export function BlockLegend({ colourBy, palette, blocks, cutoff, unit }) {
  const pal = Array.isArray(palette)
    ? palette
    : (COLORMAPS[palette] || COLORMAPS.viridis);

  const stats = useMemo(() => {
    const c = { classification: {}, lithology: {}, ore_waste: { ore: 0, waste: 0 },
                pass: {}, domain: {}, grade: { min: Infinity, max: -Infinity } };
    for (const b of blocks) {
      const g = Number(b.grade ?? b.g ?? 0);
      const isOre = g >= cutoff;
      c.ore_waste[isOre ? "ore" : "waste"]++;
      if (g < c.grade.min) c.grade.min = g;
      if (g > c.grade.max) c.grade.max = g;
      const cls = b.classification || "Unclassified";
      c.classification[cls] = (c.classification[cls] || 0) + 1;
      const lith = String(b.lithology || b.rock || "—").toUpperCase();
      c.lithology[lith] = (c.lithology[lith] || 0) + 1;
      const p = Number(b.used_pass || 0);
      c.pass[p] = (c.pass[p] || 0) + 1;
      const dom = String(b.domain || "—");
      c.domain[dom] = (c.domain[dom] || 0) + 1;
    }
    if (!Number.isFinite(c.grade.min)) c.grade = { min: 0, max: 0 };
    return c;
  }, [blocks, cutoff]);

  const rows = useMemo(() => {
    if (colourBy === "grade") return null;
    if (colourBy === "classification") {
      return ["Measured", "Indicated", "Inferred", "Unclassified"]
        .filter((k) => stats.classification[k])
        .map((k) => ({ label: k, colour: CLASS_HEX[k], count: stats.classification[k] }));
    }
    if (colourBy === "lithology") {
      return Object.entries(stats.lithology)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([k, count]) => ({
          label: k,
          colour: LITHO_HEX[k] || (k && k !== "—" ? hashHex(k) : "#94a3b8"),
          count,
        }));
    }
    if (colourBy === "ore_waste") {
      return [
        { label: `Ore (grade ≥ ${cutoff} ${unit || ""})`, colour: ORE_HEX.ore,   count: stats.ore_waste.ore },
        { label: "Waste",                                  colour: ORE_HEX.waste, count: stats.ore_waste.waste },
      ];
    }
    if (colourBy === "pass") {
      return [1, 2, 3, 0]
        .filter((k) => stats.pass[k])
        .map((k) => ({
          label: k === 0 ? "Unassigned" : `Pass ${k}`,
          colour: PASS_HEX[k], count: stats.pass[k],
        }));
    }
    if (colourBy === "domain") {
      return Object.entries(stats.domain)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([k, count], i) => ({
          label: k, count,
          colour: pal[i % pal.length],
        }));
    }
    return null;
  }, [colourBy, stats, pal, cutoff, unit]);

  return (
    <div className="absolute bottom-3 right-3 bg-slate-900/90 border border-slate-700 rounded shadow-xl p-2 max-w-[240px]"
         data-testid="cockpit-block-legend">
      <div className="text-[9px] font-mono uppercase tracking-widest text-sky-300 mb-1"
           data-testid="cockpit-legend-title">
        Legend · {({
          grade: "Grade",
          classification: "Classification",
          lithology: "Lithology",
          ore_waste: "Ore vs Waste",
          pass: "Estimation pass",
          domain: "Domain",
        }[colourBy] || colourBy)}
      </div>
      {colourBy === "grade" ? (
        <div>
          <div className="w-full h-3 rounded overflow-hidden flex">
            {pal.map((c, i) => (
              <div key={i} className="flex-1" style={{ background: c }} />
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[9px] font-mono text-slate-300">
            <span>{stats.grade.min?.toFixed(2)}</span>
            <span>{stats.grade.max?.toFixed(2)} {unit}</span>
          </div>
          <div className="text-[9px] font-mono text-slate-400 mt-0.5">
            cut-off · <b className="text-amber-300">{cutoff}</b>
          </div>
        </div>
      ) : rows && rows.length > 0 ? (
        <ul className="space-y-0.5">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2 text-[10px] font-mono"
                data-testid={`cockpit-legend-row-${r.label}`}>
              <span className="w-3 h-3 rounded border border-slate-700 shrink-0"
                     style={{ background: r.colour }} />
              <span className="flex-1 text-slate-100 truncate" title={r.label}>{r.label}</span>
              <span className="text-slate-400">{r.count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[10px] font-mono text-slate-500 italic">
          No values to display.
        </div>
      )}
    </div>
  );
}


export function DrillholeLegend({ colourBy, palette, samples, cutoff, unit }) {
  const pal = Array.isArray(palette)
    ? palette
    : (COLORMAPS[palette] || COLORMAPS.viridis);

  const stats = useMemo(() => {
    const c = { classification: {}, lithology: {}, ore_waste: { ore: 0, waste: 0 },
                 grade: { min: Infinity, max: -Infinity } };
    for (const s of samples) {
      const g = Number(s.grade || 0);
      c.ore_waste[g >= cutoff ? "ore" : "waste"]++;
      if (g < c.grade.min) c.grade.min = g;
      if (g > c.grade.max) c.grade.max = g;
      const k = String(s.lithology || "UNK").toUpperCase();
      c.lithology[k] = (c.lithology[k] || 0) + 1;
      const cls = s.classification || "Unclassified";
      c.classification[cls] = (c.classification[cls] || 0) + 1;
    }
    if (!Number.isFinite(c.grade.min)) c.grade = { min: 0, max: 0 };
    return c;
  }, [samples, cutoff]);

  const rows = useMemo(() => {
    if (colourBy === "grade") return null;
    if (colourBy === "classification") {
      return ["Measured", "Indicated", "Inferred", "Unclassified"]
        .filter((k) => stats.classification[k])
        .map((k) => ({ label: k, colour: CLASS_HEX[k], count: stats.classification[k] }));
    }
    if (colourBy === "lithology") {
      return Object.entries(stats.lithology)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 12)
        .map(([k, count]) => ({
          label: k,
          colour: LITHO_HEX[k] || (k && k !== "UNK" ? hashHex(k) : "#94a3b8"),
          count,
        }));
    }
    if (colourBy === "ore_waste") {
      return [
        { label: `Ore (≥ ${cutoff} ${unit || ""})`, colour: ORE_HEX.ore,   count: stats.ore_waste.ore },
        { label: "Waste",                            colour: ORE_HEX.waste, count: stats.ore_waste.waste },
      ];
    }
    return null;
  }, [colourBy, stats, cutoff, unit]);

  const title = { grade: "Grade", classification: "Classification",
                   lithology: "Lithology", ore_waste: "Ore vs Waste" }[colourBy] || colourBy;

  return (
    <div className="absolute bottom-3 right-3 -mb-0 translate-y-[-105%] bg-slate-900/90 border border-amber-700/60 rounded shadow-xl p-2 max-w-[240px]"
         style={{ marginBottom: "6.5rem" }}
         data-testid="cockpit-drillhole-legend">
      <div className="text-[9px] font-mono uppercase tracking-widest text-amber-300 mb-1"
           data-testid="cockpit-drillhole-legend-title">
        Drillholes · {title}
      </div>
      {colourBy === "grade" ? (
        <div>
          <div className="w-full h-3 rounded overflow-hidden flex">
            {pal.map((c, i) => (
              <div key={i} className="flex-1" style={{ background: c }} />
            ))}
          </div>
          <div className="flex justify-between mt-1 text-[9px] font-mono text-slate-300">
            <span>{stats.grade.min?.toFixed(2)}</span>
            <span>{stats.grade.max?.toFixed(2)} {unit}</span>
          </div>
        </div>
      ) : rows && rows.length > 0 ? (
        <ul className="space-y-0.5">
          {rows.map((r, i) => (
            <li key={i} className="flex items-center gap-2 text-[10px] font-mono"
                data-testid={`cockpit-drillhole-legend-row-${r.label}`}>
              <span className="w-3 h-3 rounded border border-amber-700/50 shrink-0"
                     style={{ background: r.colour }} />
              <span className="flex-1 text-slate-100 truncate" title={r.label}>{r.label}</span>
              <span className="text-slate-400">{r.count.toLocaleString()}</span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-[10px] font-mono text-slate-500 italic">
          No values to display.
        </div>
      )}
    </div>
  );
}
